// Maps a bullet's `kind` symbol to how the testbed draws it. Examples are written against these
// names. A kind that isn't listed here draws as the default bullet.

export interface Frame {
  /** A sprite file, or the key of a composed frame (see `compositions`). */
  file: string;
  sx: number;
}

/** A frame built at load time by layering sprite files, e.g. a boss body plus its engine flames. */
export interface Composition {
  w: number;
  h: number;
  flipY: boolean;
  layers: { file: string; x: number; y: number }[];
}

export const compositions: Record<string, Composition> = {};

export interface KindSpec {
  frames: Frame[];
  w: number;
  h: number;
  /** Animation speed in frames per second. 0 shows the first frame only. */
  fps: number;
  /** The heading the sprite points at as drawn, in degrees. null means don't rotate. */
  angle: number | null;
  /** Continuous spin in degrees per tick, for sprites that look better turning. */
  spin: number;
  /** World units per sprite pixel. */
  scale: number;
  /** Used when the sprite isn't available. */
  color: string;
  radius: number;
  hidden: boolean;
  /** Hit radius in field units: smaller than the sprite, as shmups make it. */
  hit: number;
}

type Options = Partial<Pick<KindSpec, 'fps' | 'angle' | 'spin' | 'scale' | 'hidden' | 'hit'>>;

const palette: [RegExp, string][] = [
  [/pink|arrow|chevron-pink/, '#ff6ad5'],
  [/magenta/, '#ff3fbf'],
  [/purple|violet|orb/, '#b07cff'],
  [/cyan|teal|ice|gem|chevron/, '#5ff2ff'],
  [/blue|bullet/, '#4f9dff'],
  [/yellow|gold|coin|shot|hyper/, '#ffd447'],
  [/orange|flame|fireball|spiky|missile/, '#ff9a3c'],
  [/red|lava/, '#ff4f4f'],
  [/green|lime|rice|leaf/, '#7dff6a'],
  [/silver|white|diamond|triangle|peach/, '#e6e2f5'],
];

function colorFor(name: string): string {
  for (const [re, color] of palette) {
    if (re.test(name)) return color;
  }
  return '#ff6ad5';
}

function radiusFor(name: string, w: number, h: number, scale: number): number {
  if (name.startsWith('tiny')) return 3;
  if (name.startsWith('small') || name === 'bullet') return 4;
  if (name.startsWith('big')) return 5;
  if (name.startsWith('huge')) return 7.5;
  return (Math.min(w, h) * scale) / 2;
}

function spec(name: string, frames: Frame[], w: number, h: number, opts: Options): KindSpec {
  const scale = opts.scale ?? 1;
  return {
    frames,
    w,
    h,
    fps: opts.fps ?? 0,
    angle: opts.angle ?? null,
    spin: opts.spin ?? 0,
    scale,
    color: colorFor(name),
    radius: radiusFor(name, w, h, scale),
    hidden: opts.hidden ?? false,
    hit: opts.hit ?? hitFor(name, w, h, scale, opts.angle ?? null),
  };
}

/**
 * A hit radius from the sprite's size. Bullets hit with their bright core, a little over half
 * their drawn radius; long thin ones by their width. Enemies hit with their body, not their fins.
 */
function hitFor(name: string, w: number, h: number, scale: number, angle: number | null): number {
  const half = (Math.min(w, h) * scale) / 2;
  const share = isEnemy(name) ? 0.45 : angle === null ? 0.6 : 0.7;
  return Math.max(1.5, Math.round(half * share * 2) / 2);
}

/** Frames picked out of a horizontal strip of `w`-wide cells. */
function strip(file: string, w: number, h: number, cells: number[], opts: Options = {}) {
  return (name: string) =>
    spec(
      name,
      cells.map((i) => ({ file, sx: i * w })),
      w,
      h,
      opts,
    );
}

/** One frame per file. */
function files(names: string[], w: number, h: number, opts: Options = {}) {
  return (name: string) =>
    spec(
      name,
      names.map((file) => ({ file, sx: 0 })),
      w,
      h,
      opts,
    );
}

/** Frames layered from several files. Each inner list is one frame, bottom layer first. */
function composed(
  key: string,
  w: number,
  h: number,
  frames: Composition['layers'][],
  opts: Options & { flipY?: boolean } = {},
) {
  const names = frames.map((layers, i) => {
    const name = `compose:${key}:${i}`;
    compositions[name] = { w, h, flipY: opts.flipY ?? false, layers };
    return name;
  });
  return files(names, w, h, opts);
}

const range = (n: number) => Array.from({ length: n }, (_, i) => i);
const up = 90;

const defs: Record<string, (name: string) => KindSpec> = {
  'tiny-pink': strip('tiny-pink.png', 7, 7, [2]),
  'tiny-purple': strip('tiny-purple.png', 7, 7, [1]),
  'tiny-blue': strip('tiny-blue.png', 6, 6, [0]),
  'tiny-cyan': strip('tiny-cyan.png', 6, 6, range(4), { fps: 8 }),
  'tiny-yellow': strip('tiny-yellow.png', 8, 8, [1]),
  'tiny-green': strip('tiny-green.png', 9, 9, [0]),
  'tiny-silver': strip('tiny-silver.png', 6, 6, [0]),

  bullet: strip('small-cyan-blue.png', 8, 8, [0]),
  'small-cyan': strip('small-cyan-blue.png', 8, 8, [0]),
  'small-blue': strip('small-cyan-blue.png', 8, 8, [3]),
  'small-yellow': strip('small-yellow-red.png', 8, 8, [0]),
  'small-red': strip('small-yellow-red.png', 8, 8, [4]),
  'small-orange': strip('small-orange-magenta.png', 8, 8, [1]),
  'small-magenta': strip('small-orange-magenta.png', 8, 8, [3]),
  'small-purple': strip('small-purple.png', 8, 8, [6]),
  'small-peach': strip('small-peach.png', 8, 8, [0]),
  'small-silver': strip('small-silver.png', 8, 8, [0]),

  'big-teal': strip('big-teal.png', 9, 9, [0]),
  'big-pink': strip('big-pink.png', 9, 9, [4]),
  'big-purple': strip('big-purple.png', 9, 9, [2]),
  'big-blue': strip('big-cyan-blue.png', 10, 10, [3]),
  'big-cyan': strip('big-cyan-blue.png', 10, 10, [0]),
  'big-yellow': strip('big-yellow.png', 10, 10, [5]),
  'big-orange': strip('big-orange-red.png', 11, 11, [2]),
  'big-red': strip('big-orange-red.png', 11, 11, [1]),

  'huge-yellow': strip('huge-orange-yellow.png', 13, 13, [0]),
  'huge-orange': strip('huge-orange-yellow.png', 13, 13, [3]),
  'huge-purple': strip('huge-purple.png', 17, 17, [0]),
  'huge-pink': strip('huge-pink.png', 13, 13, [1]),

  orb: strip('orb.png', 17, 17, range(18), { fps: 18 }),
  ring: strip('rings.png', 13, 13, [0]),
  'ring-cyan': strip('rings.png', 13, 13, [0]),
  'ring-gold': strip('rings.png', 13, 13, [1]),
  'ring-blue': strip('rings.png', 13, 13, [2]),
  'ring-orange': strip('rings.png', 13, 13, [3]),
  spiky: strip('spiky.png', 8, 8, range(7), { fps: 10, spin: 6 }),
  'spiky-thin': strip('spiky-thin.png', 8, 8, range(7), { fps: 10, spin: 6 }),
  diamond: strip('diamond.png', 8, 8, [0], { angle: up }),
  coin: strip('coin.png', 18, 15, range(6), { fps: 10 }),
  gem: strip('gem.png', 15, 15, range(9), { fps: 10 }),
  fireball: files(
    range(6).map((i) => `fireball-${i + 1}.png`),
    24,
    24,
    { fps: 12, scale: 0.75 },
  ),

  'needle-green': strip('needle-green.png', 11, 4, range(4), { fps: 8, angle: 0 }),
  'needle-cyan': strip('needle-cyan.png', 11, 4, range(4), { fps: 8, angle: 0 }),
  'needle-purple': strip('needle-purple.png', 14, 4, range(4), { fps: 8, angle: 0 }),
  'needle-violet': strip('needle-violet.png', 11, 4, range(4), { fps: 8, angle: 0 }),
  'needle-yellow': strip('needle-yellow.png', 15, 7, range(4), { fps: 8, angle: 0 }),
  'needle-orange': strip('needle-orange.png', 19, 5, range(6), { fps: 10, angle: 0 }),
  'needle-lime': strip('needle-lime.png', 13, 6, range(4), { fps: 8, angle: 0 }),
  rice: strip('rice.png', 11, 8, [2], { angle: 0 }),
  leaf: strip('leaf.png', 17, 9, range(6), { fps: 10, angle: 0 }),
  arrow: strip('arrow.png', 15, 13, [2], { angle: 0 }),
  flame: strip('flame.png', 7, 19, range(3), { fps: 10, angle: up }),
  triangle: strip('triangle.png', 10, 9, [0], { angle: up }),
  'triangle-white': strip('triangle.png', 10, 9, [1], { angle: up }),
  'triangle-orange': strip('triangle.png', 10, 9, [2], { angle: up }),
  'chevron-cyan': strip('chevron.png', 13, 26, [0], { angle: up }),
  'chevron-pink': strip('chevron.png', 13, 26, [1], { angle: up }),
  'shot-yellow': strip('shot-yellow.png', 6, 17, range(3), { fps: 12, angle: up }),
  'shot-green': strip('shot-green.png', 6, 21, range(3), { fps: 12, angle: up }),
  hyper: strip('hyper.png', 32, 32, range(2), { fps: 8, angle: up }),
  missile: strip('missile.png', 13, 18, range(5), { fps: 12, angle: up }),
  'missile-big': strip('missile-big.png', 17, 23, range(5), { fps: 12, angle: up }),
  ice: strip('ice.png', 24, 24, [0], { angle: up, scale: 0.75 }),

  // Bosses draw at half size: at 1:1 they'd cover most of the field.
  boss: files(
    range(8).map((i) => `boss1-${i + 1}.png`),
    240,
    240,
    { fps: 8, scale: 0.5 },
  ),
  // The pack draws boss 2 nose up with separate engine flames. Flip it to face the player and
  // put the flames on its two nozzles.
  'boss-2': composed(
    'boss-2',
    240,
    240,
    range(4).map((i) => [
      { file: 'boss2-body.png', x: 0, y: 0 },
      { file: `boss2-boosters-${i + 1}.png`, x: 56, y: 216 },
      { file: `boss2-boosters-${i + 1}.png`, x: 167, y: 216 },
    ]),
    { fps: 12, scale: 0.5, flipY: true },
  ),
  drone: strip('drone.png', 96, 96, [0], { scale: 0.5 }),
  'mini-beam': files(
    range(3).map((i) => `mini-beam-${i + 1}.png`),
    64,
    64,
    { fps: 8 },
  ),
  'mini-mine': strip('mini-mine.png', 64, 64, [0]),
  'mini-fire': strip('mini-fire.png', 64, 64, [0]),
  'mini-ice': strip('mini-ice.png', 64, 64, [0]),
  'mini-lightning': composed(
    'mini-lightning',
    64,
    64,
    range(3).map((i) => [
      { file: 'mini-lightning.png', x: 0, y: 0 },
      { file: `lightning-shield-${i + 1}.png`, x: 0, y: 0 },
    ]),
    { fps: 10 },
  ),
  'mini-gun': strip('mini-gun.png', 64, 64, [0]),
  emitter: strip('', 1, 1, [0], { hidden: true }),
  // A bullet that isn't drawn but still hits, unlike an emitter, which can't hit.
  hidden: strip('', 1, 1, [0], { hidden: true, hit: 3 }),
};
defs.mine = defs['mini-mine'];

// Bugs come as six-frame animation strips; the other families are single frames.
for (const family of ['bug', 'heavy', 'danger', 'cannon', 'wings', 'emperor']) {
  for (let i = 1; i <= 6; i++) {
    const def =
      family === 'bug'
        ? strip(`bug-${i}.png`, 64, 64, range(6), { fps: 10 })
        : strip(`${family}-${i}.png`, 64, 64, [0]);
    defs[`${family}-${i}`] = def;
    if (i === 1) defs[family] = def;
  }
}
const armorSizes = [
  [88, 88],
  [112, 70],
  [102, 88],
  [82, 82],
  [132, 82],
];
for (let i = 1; i <= 5; i++) {
  const [w, h] = armorSizes[i - 1];
  const def = strip(`armor-${i}.png`, w, h, [0]);
  defs[`armor-${i}`] = def;
  if (i === 1) defs.armor = def;
}

/** The enemy picker's choices, grouped as the pack groups them. */
export const enemyGroups: { label: string; options: { kind: string; label: string }[] }[] = [
  {
    label: 'Bosses',
    options: [
      { kind: 'boss', label: 'Boss 1' },
      { kind: 'boss-2', label: 'Boss 2' },
      { kind: 'drone', label: 'Boss 1 drone' },
    ],
  },
  {
    label: 'Armor',
    options: range(5).map((i) => ({ kind: `armor-${i + 1}`, label: `Armor ${i + 1}` })),
  },
  ...['bug', 'heavy', 'danger', 'cannon', 'wings', 'emperor'].map((family) => ({
    label: family[0].toUpperCase() + family.slice(1),
    options: range(6).map((i) => ({
      kind: `${family}-${i + 1}`,
      label: `${family[0].toUpperCase() + family.slice(1)} ${i + 1}`,
    })),
  })),
  {
    label: 'Minis',
    options: ['beam', 'mine', 'fire', 'ice', 'lightning', 'gun'].map((m) => ({
      kind: `mini-${m}`,
      label: `Mini ${m}`,
    })),
  },
];

export const kinds: Record<string, KindSpec> = Object.fromEntries(
  Object.entries(defs).map(([name, make]) => [name, make(name)]),
);

/** Effects the testbed plays on its own; not bullet kinds. */
export const effects = {
  hit: strip('hit.png', 14, 14, range(5), { fps: 20 })('hit'),
  explosion: strip('explosion.png', 36, 28, range(9), { fps: 20 })('explosion'),
};

export const ship = strip('ship.png', 20, 19, range(5))('ship');

/** Kind names for the help panel, in the groups people look for them by. */
export function kindGroups(): { label: string; names: string[] }[] {
  const names = Object.keys(kinds).filter((n) => !kinds[n].hidden);
  return [
    { label: 'Round bullets', names: names.filter((n) => !isEnemy(n) && kinds[n].angle === null) },
    { label: 'Bullets that face their heading', names: names.filter((n) => !isEnemy(n) && kinds[n].angle !== null) },
    { label: 'Enemies', names: names.filter((n) => isEnemy(n)) },
    { label: 'Lasers', names: Object.keys(laserKinds) },
  ];
}

/** The known kind nearest to `name` by edit distance, if it's close enough to be a typo. */
export function closestKind(name: string): string | null {
  let best: string | null = null;
  let bestDistance = Infinity;
  for (const known of [...Object.keys(kinds), ...Object.keys(laserKinds)]) {
    const d = editDistance(name, known);
    if (d < bestDistance) {
      best = known;
      bestDistance = d;
    }
  }
  return bestDistance <= Math.max(2, Math.floor(name.length / 3)) ? best : null;
}

function editDistance(a: string, b: string): number {
  let row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const next = [i];
    for (let j = 1; j <= b.length; j++) {
      next[j] = Math.min(row[j] + 1, next[j - 1] + 1, row[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    row = next;
  }
  return row[b.length];
}

/** How a laser kind draws: a slice of a beam strip tiled along the polyline, plus flames. */
export interface LaserSpec {
  /** One frame per strip cell; each is the beam body, which tiles along the length. */
  frames: { file: string; sx: number; sy: number }[];
  /** Beam body size in texels. Across the beam it stretches to the laser's width. */
  w: number;
  h: number;
  fps: number;
  /** Texels per tick the pattern flows from origin to tip. */
  flow: number;
  /** Solid colour for the warning line and for drawing without sprites. */
  color: string;
  /** Flame at the origin, and impact at the tip, while active. Strips of square-ish cells. */
  muzzle: { file: string; w: number; h: number; frames: number } | null;
  hit: { file: string; w: number; h: number; frames: number } | null;
}

function beam(
  file: string,
  w: number,
  cells: number,
  body: { y: number; h: number },
  opts: Pick<LaserSpec, 'fps' | 'flow' | 'color' | 'muzzle' | 'hit'>,
): LaserSpec {
  return { frames: range(cells).map((i) => ({ file, sx: i * w, sy: body.y })), w, h: body.h, ...opts };
}

const muzzle = {
  red: { file: 'laser-muzzle-red.png', w: 20, h: 25, frames: 3 },
  green: { file: 'laser-muzzle-green.png', w: 20, h: 25, frames: 3 },
  blue: { file: 'laser-muzzle-blue.png', w: 54, h: 71, frames: 3 },
};
const hit = {
  green: { file: 'laser-hit-green.png', w: 20, h: 25, frames: 3 },
  yellow: { file: 'laser-hit-yellow.png', w: 20, h: 25, frames: 3 },
  blue: { file: 'laser-hit-blue.png', w: 55, h: 72, frames: 3 },
};

const cyanBeam = beam(
  'laser-cyan.png',
  19,
  10,
  { y: 60, h: 160 },
  {
    fps: 15,
    flow: 0,
    color: '#5ff2ff',
    muzzle: muzzle.blue,
    hit: hit.blue,
  },
);

/**
 * Laser kinds, for `(laser ...)` and `(curvy-laser ...)`. They're separate from bullet kinds:
 * lasers come out of the VM as polylines, not sprites. `'laser` is the default.
 */
export const laserKinds: Record<string, LaserSpec> = {
  laser: cyanBeam,
  'laser-cyan': cyanBeam,
  // The blue strip repeats every 68 rows, so a three-period slice tiles seamlessly.
  'laser-blue': beam(
    'laser-blue.png',
    32,
    9,
    { y: 0, h: 204 },
    {
      fps: 12,
      flow: 4,
      color: '#4f9dff',
      muzzle: muzzle.blue,
      hit: hit.blue,
    },
  ),
  'laser-green': beam(
    'laser-green.png',
    18,
    12,
    { y: 0, h: 200 },
    {
      fps: 15,
      flow: 0,
      color: '#7dff6a',
      muzzle: muzzle.green,
      hit: hit.green,
    },
  ),
  'laser-red': beam(
    'laser-red.png',
    18,
    6,
    { y: 0, h: 200 },
    {
      fps: 12,
      flow: 0,
      color: '#ff7a3c',
      muzzle: muzzle.red,
      hit: hit.yellow,
    },
  ),
  'laser-thin': beam(
    'laser-thin.png',
    13,
    3,
    { y: 0, h: 200 },
    {
      fps: 10,
      flow: 0,
      color: '#9dff8a',
      muzzle: muzzle.green,
      hit: hit.green,
    },
  ),
};

/**
 * Every bullet kind's hit radius, for tools such as the dodge bot. Lasers aren't here: a laser
 * hits within half its width, which the VM reports.
 */
export function hitRadii(): Record<string, number> {
  return Object.fromEntries(
    Object.entries(kinds)
      .filter(([n]) => n !== 'emitter')
      .map(([n, k]) => [n, k.hit]),
  );
}

export function laserSpec(name: string): LaserSpec {
  return laserKinds[name] ?? laserKinds.laser;
}

/** Whether a name is a known kind of either sort. */
export function isKnownKind(name: string): boolean {
  return name in kinds || name in laserKinds;
}

export function kindSpec(name: string): KindSpec {
  return kinds[name] ?? kinds.bullet;
}

/** Kinds that are enemies or bosses rather than bullets. */
export function isEnemy(name: string): boolean {
  return /^(boss|drone|mine|mini-|bug|heavy|danger|cannon|wings|emperor|armor)/.test(name);
}

/** Every sprite file to load, including the layers of composed frames and the laser strips. */
export function spriteFiles(): string[] {
  const all = new Set<string>();
  for (const spec of Object.values(laserKinds)) {
    for (const f of spec.frames) all.add(f.file);
    if (spec.muzzle) all.add(spec.muzzle.file);
    if (spec.hit) all.add(spec.hit.file);
  }
  for (const spec of [...Object.values(kinds), ...Object.values(effects), ship]) {
    for (const f of spec.frames) {
      const composition = compositions[f.file];
      if (composition) for (const layer of composition.layers) all.add(layer.file);
      else if (f.file) all.add(f.file);
    }
  }
  return [...all];
}
